import ConfirmationModal from 'components/Modals/ConfirmationModal';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { PermsContextType } from 'contexts/perms.types';
import React, { useEffect, useMemo, useState } from 'react';
import { FaSort } from 'react-icons/fa';
import type { BaseTableColumnDef } from 'components/Tables/baseTablePreferences';
import { useGetOrgNameQuery } from 'services/organization/organization.api';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useDeleteRoleMutation,
  useGetAllRolesQuery,
  useUpdateRoleHierarchyMutation,
} from 'services/roles/roles.api';
import { getPermissionsMeta } from 'services/perms/perms.service';
import { selectUsername } from 'store/selectors/User/userSelectors';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import RolesHeader from './components/RolesHeader';
import GenericRolesCell from './components/TableColumns/GenericRolesCell';
import RoleActionsMenu from './components/TableColumns/RoleActionsMenu';
import RoleHierarchyCell from './components/TableColumns/RoleHierarchyCell';
import RoleUsersPopup from './components/TableColumns/RoleUsersPopup';
import { parsePermName } from '../utils/Roles.utils';
import { Role } from './types/RolesTable.types';

import {
  compareRolesByHierarchy,
  computeRolesHierarchy,
  countEnabledPermissionsLikeEditor,
  filterRoles,
  type RoleEditorPermItem,
  sortRoles,
} from './utils/RoleTableUtils';

interface RolesTableProps {
  perms: PermsContextType;
  msalInstance?: object | null;
}

export default function RolesTable({ perms, msalInstance }: RolesTableProps): JSX.Element {
  const navigate = useNavigate();
  const username = useSelector(selectUsername) as string;
  const [params, setSearchParams] = useSearchParams();

  const [showConfirmDeleteRole, setShowConfirmDeleteRole] = useState(false);
  const [showCantRemoveRole, setShowCantRemoveRole] = useState(false);
  const [hierarchy, setHierarchy] = useState<Record<string, number>>({});
  const [hierarchyChanged, setHierarchyChanged] = useState(false);
  const [originalHierarchy, setOriginalHierarchy] = useState<Record<string, number>>({});
  const [yourHighestRoleHierarchyId, setYourHighestRoleHierarchyId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<Role | null>(null);
  const { data: allRoles, isLoading, isFetching, isError } = useGetAllRolesQuery(msalInstance);
  const rolesList: Role[] = useMemo(
    () =>
      (allRoles ?? []).map((role: Role) => ({
        ...role,
        created_by_email:
          role.created_by_email ||
          (typeof role.created_by === 'string' && role.created_by.includes('@')
            ? role.created_by
            : undefined),
      })),
    [allRoles],
  );
  const { data: organizationName = '' } = useGetOrgNameQuery(undefined);
  const [deleteRole] = useDeleteRoleMutation();
  const [updateRoleHierarchy] = useUpdateRoleHierarchyMutation();
  // The editor's own permission list, so the Permissions column counts what the editor shows —
  // keys plus the section headers whose children are all on — never less than its "Check all".
  const [permList, setPermList] = useState<RoleEditorPermItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const meta = (await getPermissionsMeta())?.data?.meta;
      if (cancelled || !Array.isArray(meta)) return;
      setPermList(
        meta.map((p: { name: string; linked?: number; enableLinked?: number }, index: number) => ({
          name: parsePermName(p.name),
          linked: p.linked ?? 0,
          enableLinked: p.enableLinked ?? 0,
          index,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const { hierarchy: newHierarchy, yourHighest } = computeRolesHierarchy(rolesList, username);
    setHierarchy(newHierarchy);
    setOriginalHierarchy(newHierarchy);
    setYourHighestRoleHierarchyId(yourHighest ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesList, username]);

  useEffect(() => {
    if (JSON.stringify(hierarchy) === JSON.stringify(originalHierarchy)) {
      setHierarchyChanged(false);
    }
  }, [hierarchy, originalHierarchy]);

  const handleSearchChange = (nextValue: string) => {
    const next = new URLSearchParams(params.toString());
    if (nextValue) next.set('search', nextValue);
    else next.delete('search');
    setSearchParams(next);
  };

  // Handlers
  const handleDeleteRole = async () => {
    try {
      if (!itemToDelete) return;
      await deleteRole(itemToDelete.role_Id).unwrap();

      //remove roleid entry from hierarchy
      const newHierarchy = { ...hierarchy };
      const deleted = newHierarchy[String(itemToDelete.role_Id)];
      delete newHierarchy[String(itemToDelete.role_Id)];

      Object.keys(newHierarchy).forEach((role) => {
        if (newHierarchy[role] > deleted) newHierarchy[role] -= 1;
      });

      setHierarchy(newHierarchy);
      notifySuccessToast('Role', { isAddOperation: false, isDeleteOperation: true });
    } catch (error) {
      console.error('Failed to delete role');
      notifyErrorToast('Role', { isAddOperation: false, isDeleteOperation: true });
    } finally {
      setShowConfirmDeleteRole(false);
    }
  };

  const handleMoveUp = (roleId: number | string) => {
    const useList = [...rolesList].sort((a: Role, b: Role) =>
      compareRolesByHierarchy(a, b, hierarchy),
    );
    setHierarchyChanged(true);
    const index = useList.findIndex((num: Role) => num.role_Id === roleId);
    if (index > 0) {
      const newHierarchy = { ...hierarchy };
      const newRole = { ...useList[index] };
      const oldRole = { ...useList[index - 1] };
      newHierarchy[String(newRole.role_Id)] -= 1;
      newHierarchy[String(oldRole.role_Id)] += 1;
      setHierarchy(newHierarchy);
    }
  };

  const handleMoveDown = (roleId: number | string) => {
    const useList = [...rolesList].sort((a: Role, b: Role) =>
      compareRolesByHierarchy(a, b, hierarchy),
    );
    setHierarchyChanged(true);
    const index = useList.findIndex((num: Role) => num.role_Id === roleId);
    if (index >= 0 && index < useList.length - 1) {
      const newHierarchy = { ...hierarchy };
      const newRole = { ...useList[index] };
      const oldRole = { ...useList[index + 1] };
      newHierarchy[String(newRole.role_Id)] += 1;
      newHierarchy[String(oldRole.role_Id)] -= 1;
      setHierarchy(newHierarchy);
    }
  };

  const handleEditRole = (role: Role) => {
    navigate(`/system/editrole?roleid=${role.role_Id}`);
  };

  const handleViewRole = (role: Role) => {
    navigate(`/system/viewrole?roleid=${role.role_Id}`);
  };

  const handleRequestDeleteRole = (role: Role) => {
    if ((role.role_count ?? 0) > 0) setShowCantRemoveRole(true);
    else setShowConfirmDeleteRole(true);
    setItemToDelete(role);
  };

  // Rows
  const rows = useMemo(() => {
    if (rolesList.length === 0) return [];
    const search = params.get('search') ?? '';
    return sortRoles(filterRoles(rolesList, search), hierarchy);
  }, [rolesList, hierarchy, params]);

  // Columns
  const columns = useMemo<BaseTableColumnDef<Role>[]>(() => {
    const roleColumns: BaseTableColumnDef<Role>[] = [
      {
        header: '',
        accessorKey: 'Rank',
        Cell: ({ row, table }) => {
          const role_Id = row.original.role_Id;
          // The chevrons swap hierarchy neighbours. A sort on a sortable column shows another
          // order (the table skips a sort entry on a column that cannot sort, e.g. Rank).
          const isColumnSortActive = table
            .getState()
            .sorting.some((sort) => table.getColumn(sort.id)?.getCanSort() ?? false);
          return (
            <RoleHierarchyCell
              roleId={role_Id}
              perms={perms}
              hierarchy={hierarchy}
              yourHighestRoleHierarchyId={yourHighestRoleHierarchyId}
              onMoveUp={(id) => handleMoveUp(id)}
              onMoveDown={(id) => handleMoveDown(id)}
              isRankLocked={isColumnSortActive}
            />
          );
        },
        size: 90,
        enableSorting: false,
      },
      {
        header: 'Roles',
        accessorKey: 'role_name',
        Cell: ({ row }: { row: { original: Role } }) => {
          return <GenericRolesCell value={row.original.role_name} />;
        },
        enableSorting: true,
      },
      {
        header: 'Users',
        accessorKey: 'role_count',
        Cell: ({ row }: { row: { original: Role } }) => {
          return <RoleUsersPopup users={row.original.users} />;
        },
        size: 90,
        enableSorting: true,
      },
      {
        header: 'Permissions',
        id: 'role_perms',
        accessorFn: (role: Role) => countEnabledPermissionsLikeEditor(role.role_perms, permList),
        sortingFn: 'basic',
        Cell: ({ row }: { row: { original: Role } }) => {
          const count = countEnabledPermissionsLikeEditor(row.original.role_perms, permList);
          return <GenericRolesCell value={count === null ? '—' : String(count)} />;
        },
        size: 110,
        enableSorting: true,
      },
      {
        header: 'Created By',
        accessorKey: 'created_by_email',
        Cell: ({ row }: { row: { original: Role } }) => {
          return <GenericRolesCell value={row.original.created_by_email || 'Unavailable'} />;
        },
        size: 200,
        enableSorting: true,
      },
      {
        header: 'Date Created',
        accessorKey: 'date_created',
        accessorFn: (role: Role) =>
          role.date_created ? new Date(role.date_created).getTime() : undefined,
        Cell: ({ row }) => (
          <GenericRolesCell
            value={
              row.original.date_created
                ? new Date(row.original.date_created).toLocaleString()
                : 'Unavailable'
            }
          />
        ),
        size: 200,
      },
      {
        header: '',
        accessorKey: 'Settings',
        Cell: ({ row }: { row: { original: Role } }) => {
          const role = row.original;
          return (
            <div className="align-content-center justify-content-start">
              <RoleActionsMenu
                role={role}
                perms={perms}
                yourHighestRoleHierarchyId={yourHighestRoleHierarchyId}
                onEdit={handleEditRole}
                onDelete={handleRequestDeleteRole}
                onView={handleViewRole}
              />
            </div>
          );
        },
        size: 60,
        enableSorting: false,
      },
    ];
    return roleColumns.map((column) =>
      column.enableSorting === false
        ? column
        : {
            ...column,
            Header: ({ column: tableColumn }) => (
              <span className="d-inline-flex align-items-center gap-1">
                {column.header}
                {!tableColumn.getIsSorted() && <FaSort aria-hidden="true" />}
              </span>
            ),
          },
    );
  }, [rolesList, hierarchy, perms, yourHighestRoleHierarchyId, permList]);

  return (
    <div className="w-100 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      {organizationName && <h1 className="fs-4 mt-3 mb-2 text-break">{organizationName}</h1>}
      <RolesHeader
        searchValue={params.get('search') ?? ''}
        onSearchChange={handleSearchChange}
        perms={perms}
        hierarchyChanged={hierarchyChanged}
        onSaveHierarchy={async () => {
          try {
            await updateRoleHierarchy(hierarchy).unwrap();
          } catch (error) {
            console.error('Failed to update role hierarchy');
          } finally {
            setHierarchyChanged(false);
          }
        }}
        onDiscardHierarchy={() => {
          setHierarchy(originalHierarchy);
          setHierarchyChanged(false);
        }}
      />
      <div>
        {' '}
        <div>
          {/* Role Delete Modal */}
          {showConfirmDeleteRole && (
            <ConfirmationModal
              show={showConfirmDeleteRole}
              handleShow={() => setShowConfirmDeleteRole(false)}
              bodyTxt={`Are you sure you want to remove this role?`}
              cancelBtnTxt={'Cancel'}
              confirmBtnTxt={'Yes'}
              confirmHandler={() => {
                void handleDeleteRole();
              }}
            />
          )}
          {/* Users still in role Modal */}
          {showCantRemoveRole && (
            <ConfirmationModal
              show={showCantRemoveRole}
              handleShow={() => setShowCantRemoveRole(false)}
              bodyTxt={`The ${itemToDelete?.role_name} Role cannot be removed with ${
                itemToDelete?.role_count
              } assigned User${
                itemToDelete && itemToDelete.role_count && itemToDelete.role_count > 1 ? 's' : ''
              }. Remove all users from this role and try again.`}
              cancelBtnTxt={'Cancel'}
              confirmBtnTxt={'Ok'}
              confirmHandler={() => {
                setShowCantRemoveRole(false);
              }}
            />
          )}
        </div>
      </div>
      <div
        className="w-100 flex-grow-1 overflow-hidden d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        {/* Role Table */}
        {isLoading || (isFetching && rolesList.length === 0) ? (
          <div role="status" aria-label="Loading roles" className="w-100 flex-grow-1">
            <WidgetLoadingAnimation widgetName="" dimensions={[1, 1]} disableText />
            <span className="visually-hidden">Loading roles...</span>
          </div>
        ) : (
          <BaseTable
            rows={rows}
            tablePreferenceKey="admin-roles"
            initialState={{ columnVisibility: { date_created: false } }}
            state={{
              showProgressBars: Boolean(isFetching),
            }}
            customEmptyMessage={
              isLoading || isFetching
                ? 'Loading roles...'
                : isError
                  ? 'Unable to load roles.'
                  : 'No roles to display'
            }
            columns={columns}
            customStyles={{ container: { flex: 1, minHeight: 0 } }}
            muiTablePaperProps={{
              sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
          />
        )}
      </div>
    </div>
  );
}
